import { useEffect, useRef, useState } from "react";
import { Share2, Link as LinkIcon, Check, MessageCircle, Facebook, Linkedin, X } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareMenu — dropdown per condivisione social ottimizzato per anteprima corretta.
 * Usa gli endpoint `/api/og/{type}/{slug}` che restituiscono HTML con meta Open Graph
 * corretti (WhatsApp/Facebook/LinkedIn non eseguono JS, altrimenti vedrebbero
 * i meta generici della SPA React).
 *
 * Props:
 *   type: 'blog' | 'scuola' | 'prodotto'
 *   slug: identifier dell'entità (article.slug, school.id, product.slug)
 *   title: titolo visibile per condivisione (usato in WhatsApp text + Web Share)
 *   className: classi Tailwind extra sul bottone principale (opzionale)
 *   compact: se true, mostra solo icona (per spazi ristretti)
 */
export function ShareMenu({ type, slug, title, className = "", compact = false }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // URL "diretto" alla pagina SPA (per chi vede la preview e clicca)
  const directUrl = type === "home"
    ? "https://permanina.it/"
    : `https://permanina.it/${type === "blog" ? "blog" : type}/${slug}`;
  // URL "bot-friendly" con meta OG server-side (per l'unfurl di WhatsApp/FB/LinkedIn).
  // Per la HOME usiamo l'URL pulita: index.html contiene già i meta tag OG corretti
  // e mostrarla nel post social è molto più pulito che "permanina.it/api/og/home".
  const shareUrl = type === "home"
    ? "https://permanina.it/"
    : `https://permanina.it/api/og/${type}/${slug}`;

  const handleNativeShare = async () => {
    setOpen(false);
    if (!navigator.share) {
      // Fallback: apre il menu se Web Share API non disponibile
      setOpen(true);
      return;
    }
    try {
      await navigator.share({ title: title || "Per manina", url: shareUrl });
    } catch (e) { /* utente annulla */ }
  };

  const openWhatsApp = () => {
    const text = title ? `${title} — Per manina` : "Guarda questa pagina su Per manina";
    const wa = `https://wa.me/?text=${encodeURIComponent(text + " " + shareUrl)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const openFacebook = () => {
    const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(fb, "_blank", "width=600,height=500,noopener,noreferrer");
    setOpen(false);
  };

  const openLinkedIn = () => {
    const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(li, "_blank", "width=600,height=500,noopener,noreferrer");
    setOpen(false);
  };

  const openTwitter = () => {
    const text = title ? `${title} — Per manina` : "Per manina";
    const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(tw, "_blank", "width=600,height=500,noopener,noreferrer");
    setOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(directUrl);
      setCopied(true);
      toast.success("Link copiato");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossibile copiare");
    }
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="share-menu-toggle"
        className={
          className ||
          "btn-secondary w-full inline-flex items-center justify-center gap-2 text-sm"
        }
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Share2 size={compact ? 14 : 16} />
        {!compact && "Condividi"}
      </button>

      {open && (
        <div
          role="menu"
          data-testid="share-menu"
          className="absolute left-0 sm:left-auto sm:right-0 z-40 mt-2 w-60 rounded-lg border border-[#E8E6E1] bg-white shadow-xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#F2EFEB]">
            <span className="font-ui text-xs uppercase tracking-wider text-[#8A9691]">Condividi via</span>
            <button
              onClick={() => setOpen(false)}
              className="text-[#8A9691] hover:text-[#1A362D]"
              aria-label="Chiudi"
            >
              <X size={14} />
            </button>
          </div>
          <ul className="py-1">
            <li>
              <button
                onClick={openWhatsApp}
                data-testid="share-whatsapp"
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#F2EFEB] text-left font-ui text-sm text-[#1A362D]"
              >
                <MessageCircle size={16} className="text-[#25D366]" />
                WhatsApp
              </button>
            </li>
            <li>
              <button
                onClick={openFacebook}
                data-testid="share-facebook"
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#F2EFEB] text-left font-ui text-sm text-[#1A362D]"
              >
                <Facebook size={16} className="text-[#1877F2]" />
                Facebook
              </button>
            </li>
            <li>
              <button
                onClick={openLinkedIn}
                data-testid="share-linkedin"
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#F2EFEB] text-left font-ui text-sm text-[#1A362D]"
              >
                <Linkedin size={16} className="text-[#0A66C2]" />
                LinkedIn
              </button>
            </li>
            <li>
              <button
                onClick={openTwitter}
                data-testid="share-twitter"
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#F2EFEB] text-left font-ui text-sm text-[#1A362D]"
              >
                <span className="w-4 h-4 flex items-center justify-center text-[#1A362D] text-[13px] font-bold">𝕏</span>
                X (Twitter)
              </button>
            </li>
            <li className="border-t border-[#F2EFEB]">
              <button
                onClick={copyLink}
                data-testid="share-copy-link"
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#F2EFEB] text-left font-ui text-sm text-[#1A362D]"
              >
                {copied ? <Check size={16} className="text-[#1A362D]" /> : <LinkIcon size={16} className="text-[#5C6B64]" />}
                {copied ? "Link copiato" : "Copia link"}
              </button>
            </li>
            {typeof navigator !== "undefined" && navigator.share && (
              <li className="border-t border-[#F2EFEB]">
                <button
                  onClick={handleNativeShare}
                  data-testid="share-native"
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#F2EFEB] text-left font-ui text-sm text-[#1A362D]"
                >
                  <Share2 size={16} className="text-[#5C6B64]" />
                  Altre app…
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
